import { defineComponent as _defineComponent } from 'vue'
import TradeDefinitionFormGroup from './TradeDefinitionFormGroup.vue';
import TradeDestinationForm from './TradeDestinationForm.vue';
import CostTransparencyDetailsInfo from '../info/CostTransparencyDetailsInfo.vue';
import { TradeDestination } from './tradeDestination';
import { TradeFundsDestination, TradeDetails } from 'ah-trades/src/models/trade';
import {
  Beneficiary,
  BankingScheme,
  FeeChargeType,
  formatHedgingInstrument,
  FeePaymentType,
  AmountType,
  FeatureFlag,
  FeatureFlagTreatment,
  ComplianceStatus,
} from 'ah-api-gateways';
import { QuotePriceRequest } from 'ah-trades/src/requests/pricingRequest';
import CurrencyFundingFee from '../fees/CurrencyFundingFee.vue';
import SchedulePaymentForm from 'ah-wallets/src/components/SchedulePaymentForm.vue';
import ChargeTypeForm from 'ah-wallets/src/components/ChargeTypeForm.vue';
import PaymentReferenceForm from './PaymentReferenceForm.vue';
import PaymentReasonForm from 'ah-wallets/src/components/PaymentReasonForm.vue';
import { computed, reactive, ref, watch } from 'vue';
import { useTradeState } from '../..';
import { useOnBehalfOf } from 'ah-common-lib/src/onBehalfOf/useInjectedOBO';
import { useRequestManager } from 'ah-common-lib/src/requestManager/useRequestManager';
import { useFeatureFlag } from 'ah-common-stores';


export default /*#__PURE__*/_defineComponent({
  __name: 'TradeFormGroup',
  props: {
    tradeDetails: { default: null },
    tradePrice: { default: null },
    tradeDestination: { default: null },
    narrow: { type: [Boolean, String], default: false },
    showCancel: { type: [Boolean, String], default: false },
    beneficiary: null,
    forceKeep: { type: [Boolean, String], default: false },
    forceShowDestination: { type: [Boolean, String] },
    forceImmediateSpot: { type: [Boolean, String], default: false },
    allowedBuyCurrencies: null,
    allowedSellCurrencies: null,
    syncTradeFunds: { type: [Boolean, String], default: false },
    isPayment: { type: [String, Boolean], default: false },
    allowKeepFunds: { type: [String, Boolean], default: true },
    verifyLimits: { type: [String, Boolean], default: false },
    costTransparency: { type: [String, Boolean], default: false },
    costTransparencyCurrency: null
  },
  emits: ["update:tradePrice", "update:tradeDetails", "update:tradeDestination", "update:costTransparencyCurrency", "update:walletBalanceUsageRequired", "trade-submitted", "trade-cancelled"],
  setup(__props: any, { emit }: { emit: ({
  (e: 'update:tradePrice', value: Partial<QuotePriceRequest>): void;
  (e: 'update:tradeDetails', value: Partial<TradeDetails>): void;
  (e: 'update:tradeDestination', value: Partial<TradeDestination>): void;
  (e: 'update:costTransparencyCurrency', value: AmountType): void;
  (e: 'update:walletBalanceUsageRequired', value: boolean): void;
  (e: 'trade-submitted'): void;
  (e: 'trade-cancelled'): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { tradeDetails: TradeDetails | null, tradePrice: QuotePriceRequest | null, tradeDestination: TradeDestination | null, narrow: boolean | string, showCancel: boolean | string, beneficiary?: Beneficiary, forceKeep: boolean | string, forceShowDestination?: boolean | string, forceImmediateSpot: boolean | string, allowedBuyCurrencies?: string[], allowedSellCurrencies?: string[], syncTradeFunds: boolean | string, isPayment: string | boolean, allowKeepFunds: string | boolean, verifyLimits: string | boolean, costTransparency: string | boolean, costTransparencyCurrency: AmountType };



const tradeDestinationForm = ref<InstanceType<typeof TradeDestinationForm> | null>(null);

const tradeDefinitionFormGroup = ref<InstanceType<typeof TradeDefinitionFormGroup> | null>(null);

const schedulePaymentForm = ref<InstanceType<typeof SchedulePaymentForm> | null>(null);

const chargeTypeForm = ref<InstanceType<typeof ChargeTypeForm> | null>(null);

const paymentReferenceForm = ref<InstanceType<typeof PaymentReferenceForm> | null>(null);

const paymentReasonForm = ref<InstanceType<typeof PaymentReasonForm> | null>(null);



const state = reactive({
  loadingPrices: false as boolean,
  submittingTrade: false as boolean,
});

const requestManager = useRequestManager().manager;

const tradeState = useTradeState();

const onBehalfOfClient = useOnBehalfOf();

const authStore = tradeState.store.useAuthStore();

const walletStore = tradeState.store.useWalletsStore();

const displayCostTransparency = ref<boolean>(false);

const isActiveMarginCall = computed(() => {
  if (clientId.value) {
    return walletStore.getClientRiskProfile(clientId.value)?.collateralProfile?.activeMarginCall;
  }
  return null;
});

const clientId = computed(() => onBehalfOfClient.value?.id ?? authStore.loggedInIdentity?.client?.id);

const showCostTransparency = computed(() => props.costTransparency !== false);

const isSuspended = ref(authStore.complianceStatus === ComplianceStatus.SUSPENDED);

const isDeactivated = ref(authStore.complianceStatus === ComplianceStatus.DEACTIVATED);

const isNotPermittedToTrade = computed(() => isSuspended.value || isDeactivated.value || isActiveMarginCall.value);

const { loadFlag: loadVariationMarginFeatureFlag } = useFeatureFlag({
  featureFlag: FeatureFlag.VARIATION_MARGIN_AND_LIMITS_FEATURE,
});

function loadCostDisplayFeatureFlag() {
  const payload = {
    featureFlag: FeatureFlag.PRICING_SHOW_CLIENT_COSTS,
    oboClientId: onBehalfOfClient.value?.id,
    force: true,
  };

  if (showCostTransparency.value) {
    tradeState.store
      .useFeatureFlagStore()
      .loadFeatureFlag(payload)
      .then(
        (featureFlagResponse) =>
          (displayCostTransparency.value = featureFlagResponse?.treatment === FeatureFlagTreatment.ON)
      );
  }
}

// We skip feature flags checking in x-office to hide cost transparency in trading obo
if (!tradeState.store.useAuthStore().isAHUser) {
  loadCostDisplayFeatureFlag();
}

const isNarrow = computed(() => props.narrow !== false);

const isForceKeep = computed(() => props.forceKeep !== false);

const isForceShowDestination = computed(() => props.forceShowDestination !== false);

const isCancelShown = computed(() => props.showCancel !== false);

const isTrade = computed(
  () => !props.tradeDetails || props.tradeDetails.sellCurrency !== props.tradeDetails.buyCurrency
);

const invalidForms = computed(() => !props.tradePrice || !props.tradeDestination?.valid);

const isSendingMoney = computed(() => props.isPayment !== false);

const tradeProduct = computed(() => {
  if (!props.tradePrice) {
    return 'Trade';
  }
  return formatHedgingInstrument(props.tradePrice.instrument);
});

const targetBeneficiary = computed(() => props.tradeDestination?.beneficiary);

const minPossiblePaymentDate = computed(() => {
  if (props.tradePrice?.response?.settlementDate) {
    return new Date(props.tradePrice.response.settlementDate);
  }

  return undefined;
});

const useChargeType = computed(() => props.tradeDestination?.beneficiary?.bankingScheme === BankingScheme.SWIFT);

function onForceKeepChange() {
  if (isForceKeep.value) {
    emit('update:tradeDestination', {
      destination: TradeFundsDestination.KEEP,
      beneficiary: null,
      valid: true,
    });
  } else if (props.tradeDestination?.destination === TradeFundsDestination.SEND) {
    emit('update:tradeDestination', {
      ...props.tradeDestination,
    });
  }
}

function submitTrade() {
  // Set a submittingTrade state that will persist until loadingPrices is false, at which point trade submittal continues
  state.submittingTrade = true;
  if (state.loadingPrices) {
    return;
  }
  state.submittingTrade = false;

  schedulePaymentForm.value?.touchForms();
  chargeTypeForm.value?.touchForms();
  paymentReasonForm.value?.touchForms();
  paymentReferenceForm.value?.touchForms();

  if (tradeDefinitionFormGroup.value) {
    tradeDefinitionFormGroup.value.touchForms();
  }

  const scheduleDateInvalid = schedulePaymentForm.value?.valid === false;
  const chargeTypeInvalid = chargeTypeForm.value?.valid === false;
  const paymentReasonInvalid = paymentReasonForm.value?.valid === false;
  const paymentReferenceInvalid = paymentReferenceForm.value?.valid === false;
  const paymentOverLimit = tradeDestinationForm.value?.isPaymentInvalid === true;
  const definitionValid = tradeDefinitionFormGroup.value?.areDatesValid() === true;

  if (
    !props.tradeDetails?.valid ||
    (isTrade.value && !props.tradePrice) ||
    scheduleDateInvalid ||
    chargeTypeInvalid ||
    paymentReasonInvalid ||
    paymentReferenceInvalid ||
    !props.tradePrice ||
    !definitionValid ||
    paymentOverLimit
  ) {
    tradeState.toast.clear('tradeDetailsInfo');
    tradeState.toast.info('Please review trade details', {
      group: 'tradeDetailsInfo',
    });
    return;
  }

  if (useChargeType.value === true) {
    emit('update:tradeDestination', {
      ...props.tradeDestination,
    });
  } else {
    let tradeDestination = {
      ...props.tradeDestination,
    };

    delete tradeDestination.chargeType;

    emit('update:tradeDestination', {
      ...tradeDestination,
    });
  }

  if (
    !props.tradeDestination ||
    (props.tradeDestination?.destination === TradeFundsDestination.SEND && !props.tradeDestination?.beneficiary)
  ) {
    tradeState.toast.info('Please select a destination bank account');
    return;
  }

  emit('trade-submitted');
}

function onLoadingPricesChange() {
  if (state.loadingPrices === false && state.submittingTrade === true) {
    submitTrade();
  }
}

function onReasonUpdate(reason: string | null) {
  emit('update:tradeDestination', { ...props.tradeDestination, reason: reason ?? undefined });
}

function onReferenceUpdate(reference: string | null) {
  emit('update:tradeDestination', { ...props.tradeDestination, reference: reference ?? undefined });
}

function onChargeTypeUpdate(chargeType: FeeChargeType | null) {
  emit('update:tradeDestination', { ...props.tradeDestination, chargeType: chargeType ?? undefined });
}

function cancelTrade() {
  emit('trade-cancelled');
}

function loadClientRiskProfileReport() {
  if (!clientId?.value) {
    throw 'Cannot load client risk profile report with incomplete data';
  }

  const id = clientId.value;

  return requestManager.newPromise(
    'loadClientRiskProfileReport',
    loadVariationMarginFeatureFlag().then((flag) => {
      if (flag?.treatment === FeatureFlagTreatment.ON) {
        return walletStore.loadClientRiskProfile({
          force: true,
          clientId: id,
        });
      }
    })
  );
}

watch(
  () => state.loadingPrices,
  () => {
    onLoadingPricesChange();
  }
);

watch(
  () => [isForceKeep.value],
  () => {
    onForceKeepChange();
  },
  { immediate: true }
);

watch(
  clientId,
  () => {
    if (clientId.value) {
      loadClientRiskProfileReport();
    }
  },
  { immediate: true }
);

return { __sfc: true,emit, tradeDestinationForm, tradeDefinitionFormGroup, schedulePaymentForm, chargeTypeForm, paymentReferenceForm, paymentReasonForm, props, state, requestManager, tradeState, onBehalfOfClient, authStore, walletStore, displayCostTransparency, isActiveMarginCall, clientId, showCostTransparency, isSuspended, isDeactivated, isNotPermittedToTrade, loadVariationMarginFeatureFlag, loadCostDisplayFeatureFlag, isNarrow, isForceKeep, isForceShowDestination, isCancelShown, isTrade, invalidForms, isSendingMoney, tradeProduct, targetBeneficiary, minPossiblePaymentDate, useChargeType, onForceKeepChange, submitTrade, onLoadingPricesChange, onReasonUpdate, onReferenceUpdate, onChargeTypeUpdate, cancelTrade, loadClientRiskProfileReport, TradeDefinitionFormGroup, TradeDestinationForm, CostTransparencyDetailsInfo, FeePaymentType, CurrencyFundingFee, SchedulePaymentForm, ChargeTypeForm, PaymentReferenceForm, PaymentReasonForm }
}

})